/* global fetch */

import { TRACE_ADDIN_DEFAULT_REFRESH_TOKEN_URL } from './trace-token.constants';
import { getAccessTokenExpiryMsFromJwt } from './trace-jwt-exp.util';
import type { TraceRefreshTokenResponseEnvelope, TraceTokenData } from './trace-token-data.model';

const ACCESS_TOKEN_KEY = 'trace_jwt';
const REFRESH_TOKEN_KEY = 'traceRefreshToken';
const ACCESS_TOKEN_EXPIRY_MS_KEY = 'trace_access_exp_ms';

/** When no expiry is known, assume a bounded access lifetime (aligned with web `TokenService` fallback). */
const DEFAULT_ACCESS_FALLBACK_MS = 60 * 60 * 1000;

/**
 * Start proactive refresh this many ms before access-token expiry.
 * The web app uses 10s (`REFRESH_GRACE_MS` in `isb-shared`); the add-in uses longer headroom for refresh + ActivityHandler latency.
 */
const DEFAULT_REFRESH_GRACE_MS = 60_000;

let refreshAccessTokenInFlight: Promise<boolean> | null = null;

export interface TraceTokenStoreDeps {
    /** Override default user-manager-auth refresh URL (tests / env-specific builds). */
    refreshTokenUrl?: string;
    /** Override global `fetch` (tests). */
    fetchImpl?: typeof fetch;
}

/**
 * Persists Trace tokens in `sessionStorage` for this browsing context (tab / task-pane WebView).
 * Data is dropped when that session ends (e.g. browser tab closed); it does not persist across tabs.
 */
export class TraceTokenStore {
    constructor(private readonly deps: TraceTokenStoreDeps = {}) {}

    getTokenData(): TraceTokenData | null {
        const accessToken = sessionStorage.getItem(ACCESS_TOKEN_KEY);
        if (!accessToken) {
            return null;
        }
        const refreshToken = sessionStorage.getItem(REFRESH_TOKEN_KEY) ?? undefined;
        return refreshToken ? { accessToken, refreshToken } : { accessToken };
    }

    setTokenData(data: TraceTokenData): void {
        sessionStorage.setItem(ACCESS_TOKEN_KEY, data.accessToken);
        if (data.refreshToken) {
            sessionStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
        } else {
            sessionStorage.removeItem(REFRESH_TOKEN_KEY);
        }
        this.persistAccessExpiryFromTokenData(data);
    }

    /**
     * Connected only when both tokens exist — a lone access token is not a renewable session.
     */
    hasConnectedSession(): boolean {
        const accessToken = sessionStorage.getItem(ACCESS_TOKEN_KEY);
        const refreshToken = sessionStorage.getItem(REFRESH_TOKEN_KEY);
        return Boolean(
            accessToken &&
                accessToken.length > 0 &&
                refreshToken &&
                refreshToken.length > 0,
        );
    }

    /** Clears storage when an access token exists without a refresh token (broken / legacy state). */
    clearIncompleteSessionIfNeeded(): void {
        const accessToken = sessionStorage.getItem(ACCESS_TOKEN_KEY);
        const refreshToken = sessionStorage.getItem(REFRESH_TOKEN_KEY);
        if (accessToken && accessToken.length > 0 && (!refreshToken || refreshToken.length === 0)) {
            this.clearTokenData();
        }
    }

    clearTokenData(): void {
        sessionStorage.removeItem(ACCESS_TOKEN_KEY);
        sessionStorage.removeItem(REFRESH_TOKEN_KEY);
        sessionStorage.removeItem(ACCESS_TOKEN_EXPIRY_MS_KEY);
    }

    /**
     * Whether the access token should be refreshed soon (within `graceMs` of expiry).
     * Requires both access and refresh tokens; returns false if expiry cannot be determined.
     */
    shouldRefreshAccessToken(graceMs: number = DEFAULT_REFRESH_GRACE_MS): boolean {
        const tokenData = this.getTokenData();
        if (!tokenData?.accessToken || !tokenData.refreshToken) {
            return false;
        }
        const expMs = this.getResolvedAccessExpiryMs();
        if (expMs === null) {
            return false;
        }
        return Date.now() + graceMs >= expMs;
    }

    /**
     * Calls user-manager-auth refresh; on success updates stored tokens and expiry. On failure clears storage.
     * Concurrent callers share one in-flight refresh (same pattern as `isb-shared` interceptor).
     */
    async refreshAccessToken(): Promise<boolean> {
        if (refreshAccessTokenInFlight !== null) {
            return refreshAccessTokenInFlight;
        }
        refreshAccessTokenInFlight = this.performRefresh().finally(() => {
            refreshAccessTokenInFlight = null;
        });
        return refreshAccessTokenInFlight;
    }

    private getResolvedAccessExpiryMs(): number | null {
        const stored = sessionStorage.getItem(ACCESS_TOKEN_EXPIRY_MS_KEY);
        if (stored) {
            const ms = Number(stored);
            if (Number.isFinite(ms)) {
                return ms;
            }
        }
        const tokenData = this.getTokenData();
        if (!tokenData?.accessToken) {
            return null;
        }
        return getAccessTokenExpiryMsFromJwt(tokenData.accessToken);
    }

    private persistAccessExpiryFromTokenData(data: TraceTokenData): void {
        const iso = data.expiryDatetime?.trim();
        if (iso) {
            const ms = Date.parse(iso);
            if (Number.isFinite(ms)) {
                sessionStorage.setItem(ACCESS_TOKEN_EXPIRY_MS_KEY, String(ms));
                return;
            }
        }
        if (
            typeof data.expiresInSeconds === 'number' &&
            Number.isFinite(data.expiresInSeconds) &&
            data.expiresInSeconds >= 0
        ) {
            sessionStorage.setItem(
                ACCESS_TOKEN_EXPIRY_MS_KEY,
                String(Date.now() + Math.floor(data.expiresInSeconds) * 1000),
            );
            return;
        }
        const jwtMs = getAccessTokenExpiryMsFromJwt(data.accessToken);
        if (jwtMs !== null) {
            sessionStorage.setItem(ACCESS_TOKEN_EXPIRY_MS_KEY, String(jwtMs));
            return;
        }
        sessionStorage.setItem(
            ACCESS_TOKEN_EXPIRY_MS_KEY,
            String(Date.now() + DEFAULT_ACCESS_FALLBACK_MS),
        );
    }

    private async performRefresh(): Promise<boolean> {
        const tokenData = this.getTokenData();
        if (!tokenData?.refreshToken || !tokenData.accessToken) {
            this.clearTokenData();
            return false;
        }

        const fetchFn = this.deps.fetchImpl ?? fetch;
        const refreshUrl = this.deps.refreshTokenUrl ?? TRACE_ADDIN_DEFAULT_REFRESH_TOKEN_URL;

        try {
            const response = await fetchFn(refreshUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    refreshToken: tokenData.refreshToken,
                    accessToken: tokenData.accessToken,
                }),
            });

            const body = (await response.json().catch(() => null)) as TraceRefreshTokenResponseEnvelope | null;
            const data = body?.data;
            if (!response.ok || !data?.accessToken) {
                this.clearTokenData();
                return false;
            }

            this.setTokenData({
                accessToken: data.accessToken,
                refreshToken: data.refreshToken ?? tokenData.refreshToken,
                expiresInSeconds: data.expiresInSeconds,
                expiryDatetime: data.expiryDatetime,
            });
            return true;
        } catch {
            this.clearTokenData();
            return false;
        }
    }
}

/** Shared instance for the task pane. */
export const traceTokenStore = new TraceTokenStore();
